import { FolderCard } from "./FolderCard";
import { useFolderNavigation } from "./useFolderNavigation";
import { Button, Input, Col, Empty, Card, Row, message, Modal, Space } from "antd";
import { ArrowLeftOutlined, ReloadOutlined, FolderAddOutlined, DownloadOutlined } from "@ant-design/icons";
import { FolderBreadcrumb } from "./FolderBreadcrumb";
import { useState, useEffect, useCallback, useRef } from "react";
import { useSelector } from "react-redux";
import { selectAppPermissions } from "../../store/auth/selectors";
import { checkFolderName, createFolder } from "../../services/project/checkFolder";
import { downloadFolder } from "../../services/proyectoEstructurado/proyectoEstructuracionTreeService";
import { FOLDER_NAME_REGEX } from "../../services/proyectoEstructurado/proyectoEstructuradoFolderActions";
import { logAction } from "../Poa/logActionsPOA";

const { Search } = Input;
const NODE_RAIZ = "DocumentosPOA";

export function FolderNavigator({
  codigo_tarea,
  filtro,
  data,
  className,
  loading = false,   // ✅ nuevo prop: indica refresh en curso sin desmontar
  onRefresh,
  onInvalidateCache,
  onNavigationChange,
  preservedNavigationState,
  rootPath,
}) {
  const initialNavigationState = useRef(preservedNavigationState || null);

  const {
    navigationState,
    filteredItems,
    searchFilter,
    setSearchFilter,
    navigateToFolder,
    navigateBack,
    navigateToBreadcrumb,
    canNavigateBack,
    updateFileCount,
    updateData,
    rootData,
    currentFolderPath,
  } = useFolderNavigation(
    data,
    initialNavigationState.current,
    // ✅ Pasar contexto para el ordenamiento visual de actas
    codigo_tarea,
    filtro,
    rootPath,
  );

  // ── Permisos del CRUD de carpetas (nuevo app_name dedicado) ─────────────
  const folderPermissions = useSelector((state) =>
    selectAppPermissions(state, "proyecto-estructurado-manager")
  );
  const permissionAccess = folderPermissions.priv_access === "Y";
  const permissionInsert = folderPermissions.priv_insert === "Y";
  const permissionUpdate = folderPermissions.priv_update === "Y";
  const permissionDelete = folderPermissions.priv_delete === "Y";
  const permissionExport = folderPermissions.priv_export === "Y";
  const canCreate = permissionAccess && permissionInsert;
  const canDownload = permissionAccess && permissionExport;
  const cardPermissions = {
    canUpdate: permissionAccess && permissionUpdate,
    canDelete: permissionAccess && permissionDelete,
    canExport: canDownload,
  };

  const user = useSelector((state) => state.auth.user);
  const userLogin = user?.username;

  // ── Crear carpeta (en la carpeta actualmente navegada) ───────────────────
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");
  const [creatingFolder, setCreatingFolder] = useState(false);
  const [downloadingCurrentFolder, setDownloadingCurrentFolder] = useState(false);

  const handleNewFolderNameChange = useCallback((e) => {
    const value = e.target.value.normalize("NFC");
    if (FOLDER_NAME_REGEX.test(value) || value === "") {
      setNewFolderName(value);
    } else {
      message.error("El nombre contiene caracteres no permitidos.");
    }
  }, []);

  const handleCreateFolderConfirm = useCallback(async () => {
    if (!newFolderName.trim()) {
      message.warning("Debes ingresar un nombre para la carpeta.");
      return;
    }
    if (!currentFolderPath) {
      message.warning("No se pudo determinar la carpeta actual.");
      return;
    }
    setCreatingFolder(true);
    try {
      const check = await checkFolderName(currentFolderPath, newFolderName, NODE_RAIZ);
      if (check.data.status === "error") {
        message.error(check.data.message);
        return;
      }
      const fullPath = `${NODE_RAIZ}/${currentFolderPath}/${newFolderName}`.replace(/\/{2,}/g, "/");
      const resourceInfo = logAction(userLogin, "createFolder", newFolderName, {
        nameResource: newFolderName,
        Path: currentFolderPath,
        fatherResource: `${NODE_RAIZ}/${currentFolderPath}`.replace(/\/{2,}/g, "/"),
        typeResource: "folder",
        key: `${currentFolderPath}/${newFolderName}`.replace(/\/{2,}/g, "/"),
        fullPath,
        resourceType: "Folder ProyectoEstructurado",
      });
      await createFolder(currentFolderPath, newFolderName, NODE_RAIZ, resourceInfo);
      message.success("Carpeta creada exitosamente");
      setIsCreateModalOpen(false);
      setNewFolderName("");
      if (onInvalidateCache) await onInvalidateCache(currentFolderPath);
      if (onRefresh) await onRefresh();
    } catch (error) {
      console.error("Error al crear la carpeta:", error);
      message.error("Ocurrió un error al crear la carpeta.");
    } finally {
      setCreatingFolder(false);
    }
  }, [newFolderName, currentFolderPath, userLogin, onInvalidateCache, onRefresh]);

  // ── Descargar la carpeta actualmente navegada ─────────────────────────────
  const handleDownloadCurrentFolder = useCallback(async () => {
    if (!currentFolderPath) {
      message.warning("No se pudo determinar la carpeta actual.");
      return;
    }
    setDownloadingCurrentFolder(true);
    try {
      const response = await downloadFolder(currentFolderPath);
      const blob = new Blob([response], { type: "application/zip" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      const currentName =
        navigationState.breadcrumbs[navigationState.breadcrumbs.length - 1]?.name || "carpeta";
      link.href = url;
      link.download = `${currentName}.zip`;
      document.body.appendChild(link);
      link.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(link);
      message.success("Descarga completa");
    } catch (error) {
      console.error("Error al descargar la carpeta:", error);
      message.error("Error al descargar la carpeta");
    } finally {
      setDownloadingCurrentFolder(false);
    }
  }, [currentFolderPath, navigationState.breadcrumbs]);

  // Efecto para actualizar datos cuando cambian
  useEffect(() => {
    if (data && JSON.stringify(data) !== JSON.stringify(rootData)) {
      updateData(data);
    }
  }, [data, rootData, updateData]);

  // Efecto para notificar cambios de navegación
  useEffect(() => {
    if (onNavigationChange) {
      onNavigationChange(navigationState);
    }
  }, [navigationState, onNavigationChange]);

  // ─────────────────────────────────────────────────────────────────────
  // handleFileUpload: invalida cache de la ruta específica y luego
  // hace refresh completo. Se pasa a FolderCard para que lo llame
  // tras subir/eliminar en FileManagerPOA.
  // ─────────────────────────────────────────────────────────────────────
  const handleFileUpload = useCallback(async (file, folderPath) => {
    try {
      if (onInvalidateCache) {
        await onInvalidateCache(folderPath);
      }
      // Pequeña espera para asegurar que el servidor haya procesado el archivo
      await new Promise(resolve => setTimeout(resolve, 500));
      if (onRefresh) {
        await onRefresh();
      }
    } catch (error) {
      console.error('❌ Error al subir archivo:', error);
      message.error('Error al subir el archivo');
    }
  }, [onInvalidateCache, onRefresh]);

  const handleRefresh = useCallback(() => {
    if (onRefresh) {
      onRefresh();
    }
  }, [onRefresh]);

  return (
    <div className={className} style={{ padding: "4px" }}>
      {/* Header */}
      <Row gutter={24} style={{ marginBottom: 16 }}>
        <Col span={24}>
          <FolderBreadcrumb
            breadcrumbs={navigationState.breadcrumbs}
            onNavigate={navigateToBreadcrumb}
          />
        </Col>
      </Row>

      {/* Search */}
      <Row gutter={16} align="middle" wrap={false} style={{ marginBottom: 16 }}>
        <Col flex="auto">
          <Search
            placeholder="Buscar carpetas o archivos"
            value={searchFilter}
            onChange={(e) => setSearchFilter(e.target.value)}
            allowClear
          />
        </Col>

        <Col flex="none">
          <Space size={8} wrap={false}>
            <Button
              type="default"
              icon={<ReloadOutlined spin={loading} />}
              onClick={handleRefresh}
              loading={loading}
            >
              {loading ? "Actualizando..." : "Actualizar Contenido"}
            </Button>

            {canCreate && (
              <Button
                type="default"
                icon={<FolderAddOutlined />}
                onClick={() => setIsCreateModalOpen(true)}
              >
                Crear Carpeta
              </Button>
            )}

            {canDownload && (
              <Button
                type="default"
                icon={<DownloadOutlined />}
                onClick={handleDownloadCurrentFolder}
                loading={downloadingCurrentFolder}
              >
                Descargar Carpeta
              </Button>
            )}

            {canNavigateBack && (
              <Button
                type="default"
                icon={<ArrowLeftOutlined />}
                onClick={navigateBack}
              >
                Volver
              </Button>
            )}
          </Space>
        </Col>
      </Row>

      <Row gutter={24} style={{ marginBottom: 16 }}>
        <Col span={24}>
          {filteredItems.length > 0 ? (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
                gap: "16px",
              }}
            >
              {filteredItems.map((item) => (
                <Card
                  key={item.key}
                  hoverable
                  styles={{ body: { padding: 12 } }}
                  style={{ height: "100%" }}
                >
                  <FolderCard
                    codigo_tarea={codigo_tarea}
                    filtro={filtro}
                    item={item}
                    onClick={navigateToFolder}
                    isNavigable={true}
                    onUpdateFileCount={updateFileCount}
                    onFileUpload={handleFileUpload}
                    // ✅ onRefresh encadenado: invalida cache + reload completo
                    onRefresh={handleFileUpload}
                    permissions={cardPermissions}
                  />
                </Card>
              ))}
            </div>
          ) : (
            <Empty
              description={
                searchFilter
                  ? "No se encontraron resultados"
                  : "No hay elementos para mostrar"
              }
              style={{ padding: 48 }}
            />
          )}
        </Col>
      </Row>

      {/* ── MODAL: CREAR CARPETA (en la carpeta actualmente navegada) ────── */}
      <Modal
        title="Crear nueva carpeta"
        open={isCreateModalOpen}
        onOk={handleCreateFolderConfirm}
        confirmLoading={creatingFolder}
        onCancel={() => {
          setIsCreateModalOpen(false);
          setNewFolderName("");
        }}
        okText="Crear"
        cancelText="Cancelar"
      >
        <Input
          placeholder="Nombre de la nueva carpeta"
          value={newFolderName}
          onChange={handleNewFolderNameChange}
          onPressEnter={handleCreateFolderConfirm}
        />
      </Modal>
    </div>
  );
}